/**
 * Customer Payment History Page
 *
 * Displays paginated payment transaction history for the authenticated customer.
 * Supports filtering by type (all/payments/refunds) and receipt download links.
 *
 * Epic: MVP 1.21.6 - Revenue Dashboard & Stripe-Dependent Features
 * Story: SCRUM-543 - Customer Payment History (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/payment-history
 * - Receipt download via GET /api/v1/customer/bookings/:bookingId/receipt
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  DollarSign,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  CreditCard
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const STATUS_STYLES = {
  completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Processing' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
  reversed: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Reversed' }
};

const TYPE_LABELS = {
  payment: 'Payment',
  refund: 'Refund',
  partial_refund: 'Partial Refund',
  payout: 'Payout',
  dispute: 'Dispute',
  dispute_reversal: 'Dispute Reversal'
};

function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
}

function TypeBadge({ type }) {
  const label = TYPE_LABELS[type] || type;
  const isRefund = type === 'refund' || type === 'partial_refund';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isRefund ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
    }`}>
      {label}
    </span>
  );
}

function formatCents(cents) {
  if (cents === null || cents === undefined) { return '$0.00'; }
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(dateStr) {
  if (!dateStr) { return '—'; }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function PaymentHistory() {
  const navigate = useNavigate();
  const handleNavigate = (id, path) => { if (path) { navigate(path); } };
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, has_more: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  const fetchHistory = useCallback(async (offset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: '20',
        offset: String(offset),
        type: typeFilter
      });
      const response = await apiRequest(`/api/v1/customer/payment-history?${params}`);
      if (response.success) {
        setTransactions(response.data.transactions);
        setPagination(response.data.pagination);
      }
    } catch (err) {
      setError(err.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => {
    fetchHistory(0);
  }, [fetchHistory]);

  const handlePrevPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchHistory(newOffset);
  };

  const handleNextPage = () => {
    if (pagination.has_more) {
      fetchHistory(pagination.offset + pagination.limit);
    }
  };

  const handleDownloadReceipt = async (bookingId) => {
    setDownloadingReceipt(bookingId);
    try {
      const response = await apiRequest(`/api/v1/customer/bookings/${bookingId}/receipt`, {
        responseType: 'blob'
      });
      const blob = new Blob([response], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt-${bookingId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      setError(`Failed to download receipt: ${err.message}`);
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;

  return (
    <DashboardLayout userRole="customer" activeNavId="settings" onNavigate={handleNavigate}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-6 h-6" />
            Payment History
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            View your payment transactions and download receipts.
          </p>
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-3">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm text-sm focus:border-blue-500 focus:ring-blue-500"
            aria-label="Filter by type"
          >
            <option value="all">All Transactions</option>
            <option value="payment">Payments Only</option>
            <option value="refund">Refunds Only</option>
          </select>
          <span className="text-sm text-gray-500">
            {pagination.total} transaction{pagination.total !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Error */}
        {error && (
          <ErrorAlert message={error} className="mb-4" />
        )}

        {/* Loading */}
        {loading && (
          <LoadingSpinner size="md" className="py-12" />
        )}

        {/* Empty State */}
        {!loading && transactions.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
            <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No payment history yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Your payment transactions will appear here after booking an experience.
            </p>
          </div>
        )}

        {/* Transaction List */}
        {!loading && transactions.length > 0 && (
          <div className="bg-white shadow-sm rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Experience</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Card</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((txn) => (
                  <tr key={txn.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(txn.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{txn.booking?.experience_title || '—'}</div>
                      {txn.booking?.guide_name && (
                        <div className="text-xs text-gray-500">Guide: {txn.booking.guide_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TypeBadge type={txn.type} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCents(txn.amount_cents)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {txn.card_brand && txn.card_last4 ? (
                        <span className="capitalize">{txn.card_brand} ****{txn.card_last4}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={txn.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {txn.receipt_available ? (
                        <button
                          onClick={() => handleDownloadReceipt(txn.booking?.id)}
                          disabled={downloadingReceipt === txn.booking?.id}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
                        >
                          <Download className="w-4 h-4" />
                          {downloadingReceipt === txn.booking?.id ? 'Downloading...' : 'Receipt'}
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.total > pagination.limit && (
              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t">
                <div className="text-sm text-gray-500">
                  Page {currentPage} of {totalPages} ({pagination.total} total)
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={pagination.offset === 0}
                    className="inline-flex items-center px-3 py-1 rounded-md text-sm border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={!pagination.has_more}
                    className="inline-flex items-center px-3 py-1 rounded-md text-sm border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
